/**
 * When a moment arrives on the watch page, and when it goes.
 *
 * The host sets this on the board ("During the talk"): where a moment lands,
 * whether the talk pauses behind it, and how long it stays. The viewer's player
 * has to honour that exactly, and it has to honour three rules the host was
 * promised:
 *
 *   • Never a stack. One moment is on screen at a time; a second one due while
 *     the first is up waits its turn behind the "1 waiting" pill.
 *   • Once per viewer. A moment they answered never arrives, and one that has
 *     already been up never comes back, however many times they replay, unless
 *     they ask for it again themselves from its dot on the bar.
 *   • Only crossing the minute fires it. Seeking past a moment does not, so
 *     dragging the scrub bar cannot dump five cards on somebody.
 *
 * Everything here is pure: no clock, no DOM, no network. The player feeds it
 * events and reads back what is up, what waits, and whether to pause. The one
 * thing it cannot decide on its own is the passing of time, so the stay timer
 * lives in the component and comes back as an `expire` event.
 */
import type { BoardArrive } from "../../shared/types";

/** One moment as the scheduler needs it, whatever kind it is. */
export interface ArrivalMoment {
  id: string;
  /** Seconds into the talk it is due. */
  appears_at: number;
  show: BoardArrive;
}

export interface ArrivalState {
  /** The moment on screen, or null when the player is clear. */
  up: string | null;
  /**
   * Has the one on screen been answered? It stays a beat longer so they read the
   * thanks, but the talk is free again the instant they answer, and the card is
   * on its way out.
   */
  answered: boolean;
  /** The ones due while another was up, in the order they became due. */
  queue: readonly string[];
  /** Every moment that has had its turn, or that this viewer already answered. */
  done: ReadonlySet<string>;
}

export type ArrivalEvent =
  /** The playhead moved. Only a real playing step fires anything. */
  | {
      type: "time";
      from: number;
      to: number;
      playing: boolean;
      /** Seconds of WALL clock since the previous tick: what a seek is judged against. */
      wall: number;
      /** The speed the talk is playing at, when it is not 1x. */
      rate?: number;
    }
  /** They answered inside the card (or from the Moments tab). */
  | { type: "answer"; id: string }
  /** They tapped Later, pressed Escape, or closed it. */
  | { type: "dismiss"; id: string }
  /** Its stay ran out. */
  | { type: "expire"; id: string }
  /**
   * They pressed its dot on the bar. One they have not answered may arrive once
   * more when the talk crosses it; the caller checks it is unanswered.
   */
  | { type: "again"; id: string }
  /**
   * A live's moment is up now: the host started it, or its minute came. It takes the
   * screen at once, and the one there steps aside, since a live cannot wait for it.
   */
  | { type: "due"; id: string }
  /** The host closed or skipped it: it goes, and never comes back. */
  | { type: "close"; id: string };

/**
 * How far past the wall clock a playhead step may run and still be playback.
 *
 * `timeupdate` normally fires about four times a second, but a busy main thread,
 * a stalled buffer or a backgrounded tab can hold it off for seconds, and the
 * talk really did play through those seconds. So a step is judged against the
 * WALL clock, not a fixed ceiling: playing for three seconds may move the
 * playhead three seconds, and every moment in there is due. A seek moves the
 * playhead far beyond anything the wall clock could account for, and fires
 * nothing. The margin covers timer jitter and rounding.
 */
export const PLAY_STEP_MARGIN_SEC = 0.5;

/** Was this playhead step the talk playing, or the viewer jumping? */
export function isPlaybackStep(step: number, wall: number, rate = 1): boolean {
  if (step <= 0) return false;
  const speed = Number.isFinite(rate) && rate > 0 ? rate : 1;
  const wallSeconds = Number.isFinite(wall) && wall > 0 ? wall : 0;
  return step <= wallSeconds * speed + PLAY_STEP_MARGIN_SEC;
}

/** How long an answered moment stays, so the thanks is read before it goes. */
export const ANSWERED_STAY_SEC = 2;

export function emptyArrival(done: Iterable<string> = []): ArrivalState {
  return { up: null, answered: false, queue: [], done: new Set(done) };
}

/**
 * How long an unanswered moment stays up, in seconds, or null when it is not on
 * a clock at all.
 *
 * A moment that pauses the talk is never on a clock: the talk is waiting on
 * them, so it stays until they answer or tap Later. "Until they answer" is the
 * same. "Until the next moment" has no clock either: it goes when the next one
 * arrives, and stays if none ever does.
 */
export function staySeconds(show: BoardArrive): number | null {
  if (show.pause) return null;
  if (show.stay === "15") return 15;
  if (show.stay === "45") return 45;
  return null;
}

function withDone(state: ArrivalState, id: string): ReadonlySet<string> {
  const next = new Set(state.done);
  next.add(id);
  return next;
}

/** The moment on screen, resolved against the list. */
export function upMoment(
  state: ArrivalState,
  moments: readonly ArrivalMoment[],
): ArrivalMoment | null {
  return moments.find((m) => m.id === state.up) ?? null;
}

/** How many are waiting behind the one on screen (what the pill counts). */
export function waitingCount(state: ArrivalState): number {
  return state.queue.length;
}

/**
 * Should the talk be paused right now? Only for a moment that asks for it, and
 * only until they answer: answering is what the talk was waiting for.
 */
export function shouldPause(
  state: ArrivalState,
  moments: readonly ArrivalMoment[],
): boolean {
  return !state.answered && upMoment(state, moments)?.show.pause === true;
}

/** Whoever is next in the queue takes the screen, or the screen goes clear. */
function goNext(state: ArrivalState): ArrivalState {
  const [next, ...rest] = state.queue;
  if (!next) return { ...state, up: null, answered: false, queue: [] };
  return { up: next, answered: false, queue: rest, done: withDone(state, next) };
}

/**
 * One moment becomes due.
 *
 * Nothing is up: it arrives. Something is up: it waits, EXCEPT when the one on
 * screen is already done with (answered, or staying only "until the next
 * moment", which is exactly this one), and then that one steps aside.
 */
function becomeDue(
  state: ArrivalState,
  moments: readonly ArrivalMoment[],
  id: string,
): ArrivalState {
  if (state.up === null) {
    return { up: id, answered: false, queue: state.queue, done: withDone(state, id) };
  }
  // An answered card keeps the screen for its beat: the thanks is the last thing
  // it has to say, and a moment landing a second later must not eat it.
  if (state.answered) return { ...state, queue: [...state.queue, id] };
  const current = upMoment(state, moments);
  if (current && !current.show.pause && current.show.stay === "next") {
    return { up: id, answered: false, queue: state.queue, done: withDone(state, id) };
  }
  return { ...state, queue: [...state.queue, id] };
}

/** Is this moment already spoken for: on screen, waiting, or finished with? */
function claimed(state: ArrivalState, id: string): boolean {
  return state.up === id || state.done.has(id) || state.queue.includes(id);
}

/**
 * Which moments a playing step crossed, in the order they land in the talk.
 * Exclusive of `from`, inclusive of `to`, so a step never fires the same moment
 * twice and never fires one that is only just ahead.
 */
export function dueBetween(
  state: ArrivalState,
  moments: readonly ArrivalMoment[],
  from: number,
  to: number,
): ArrivalMoment[] {
  return moments
    .filter((m) => !claimed(state, m.id) && m.appears_at > from && m.appears_at <= to)
    .toSorted((a, b) => a.appears_at - b.appears_at);
}

/** The whole scheduler, as one pure step. */
export function arrivalReducer(
  state: ArrivalState,
  moments: readonly ArrivalMoment[],
  event: ArrivalEvent,
): ArrivalState {
  switch (event.type) {
    case "time": {
      // Paused, rewound, or a jump the wall clock cannot account for: nothing is due.
      if (!event.playing) return state;
      if (!isPlaybackStep(event.to - event.from, event.wall, event.rate)) return state;
      const due = dueBetween(state, moments, event.from, event.to);
      if (due.length === 0) return state;
      // At most ONE handover per tick. The first one due may take the screen; the
      // rest queue, so two moments a second apart are still read one at a time.
      let next = becomeDue(state, moments, due[0].id);
      for (const moment of due.slice(1)) next = { ...next, queue: [...next.queue, moment.id] };
      return next;
    }
    case "answer": {
      // Answered anywhere counts: the Moments tab and the card on the player
      // write the same response, and either way it never arrives again. The one
      // on screen keeps the screen for its beat, showing what the answer did.
      const done = withDone(state, event.id);
      if (state.up !== event.id) {
        return { ...state, done, queue: state.queue.filter((id) => id !== event.id) };
      }
      return { ...state, answered: true, done };
    }
    case "dismiss":
    case "expire":
      // Only the one on screen can be dismissed or run out.
      return state.up === event.id ? goNext(state) : state;
    case "again": {
      if (state.up === event.id || !state.done.has(event.id)) return state;
      const done = new Set(state.done);
      done.delete(event.id);
      return { ...state, done };
    }
    case "due": {
      if (claimed(state, event.id)) return state;
      // An answered card keeps the screen for its beat, as it does for a talk's.
      if (state.up !== null && state.answered) return { ...state, queue: [...state.queue, event.id] };
      return { up: event.id, answered: false, queue: [], done: withDone(state, event.id) };
    }
    case "close": {
      if (state.up === event.id) return goNext(state);
      if (state.done.has(event.id) && !state.queue.includes(event.id)) return state;
      return { ...state, done: withDone(state, event.id), queue: state.queue.filter((id) => id !== event.id) };
    }
  }
}

// ── answering, and only then using the moment up ─────────────────────────
/**
 * Answering a moment, in the one order that cannot lose it.
 *
 * The card shows the result straight away (`optimistic`), but the moment is only
 * used up once the answer really landed: `settled` runs on success, and that is
 * where the `answer` event and the browser's own memory of it belong. A refused
 * or dropped post runs `failed` instead, the card stays where it is, and the
 * moment is still theirs to answer.
 *
 * Nothing ambient here either: the post itself is handed in.
 */
export async function settleAnswer(handlers: {
  optimistic: () => void;
  post: () => Promise<unknown>;
  settled: () => void;
  failed: () => void;
}): Promise<boolean> {
  handlers.optimistic();
  try {
    await handlers.post();
  } catch {
    handlers.failed();
    return false;
  }
  handlers.settled();
  return true;
}
